import { Conversation, IMessage, PageContainer, Spinner, useToast } from '@color/continuum';
import {
  IErrorPayload,
  logAnalyticsPageView,
  trackAnalyticsEvent,
  useAuthContext,
} from '@color/lib';
import { useOnDataChanged, useOnQueryError } from '@color/lib/QueryUtils';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Smooch from 'smooch';

import {
  useBulkUpdateMessagesToReadMutation,
  useSmoochGetConversationByIdQuery,
  useUpdateConversationLastOpenedAtByPatientMutation,
  useUpdateMessageToReadMutation,
} from '../apis/api';
import { smoochSendMessageInConversation } from '../apis/smooch_api_utils';
import { PATIENT_MESSAGING_INBOX_PATH } from '../paths';
import { IConversationWithMessageIds, IZendeskConversation, IZendeskMessage } from '../types';
import {
  convertZendeskConversationToInternalRepresentation,
  convertZendeskMessageToInternalRepresentation,
} from '../utils';

export const ConversationDetailView = () => {
  const history = useHistory();
  const { currentUser } = useAuthContext();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [conversation, setConversation] = useState<IConversationWithMessageIds>();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const { showErrorToast } = useToast();
  const { mutateAsync: bulkUpdateMessagesToRead } = useBulkUpdateMessagesToReadMutation();
  const { mutateAsync: updateMessageToRead } = useUpdateMessageToReadMutation();
  const { mutateAsync: updateConversationLastOpenedAtByPatient } =
    useUpdateConversationLastOpenedAtByPatientMutation();

  const smoochGetConversationByIDQueryResponse = useSmoochGetConversationByIdQuery(conversationId);
  useOnDataChanged(
    smoochGetConversationByIDQueryResponse,
    (fetchedConversation: IZendeskConversation) => {
      if (fetchedConversation.messages.length === 0) {
        // We don't expect a conversation to ever have no messages, so this is an invalid state.
        showErrorToast('Conversation has no messages. Please contact support to fix this problem.');
        history.push(PATIENT_MESSAGING_INBOX_PATH);
      } else {
        setConversation(convertZendeskConversationToInternalRepresentation(fetchedConversation));
        setMessages(
          fetchedConversation.messages.reduce(
            (messageList: IMessage[], currentMessage: IZendeskMessage) =>
              // Filter out messages that are hidden from patient or have no text content
              // Some messages may not have text (e.g. images)
              !currentMessage.metadata?.is_hidden_from_patient && currentMessage.text
                ? [...messageList, convertZendeskMessageToInternalRepresentation(currentMessage)]
                : messageList,
            []
          )
        );
        if (fetchedConversation.messages.at(-1) !== undefined) {
          bulkUpdateMessagesToRead({
            conversation_external_id: fetchedConversation.id,
            message_external_id: fetchedConversation.messages.at(-1)!.id,
          });
          updateConversationLastOpenedAtByPatient({
            conversationExternalId: fetchedConversation.id,
          });
        }
      }
    }
  );
  useOnQueryError(smoochGetConversationByIDQueryResponse, (error: IErrorPayload) => {
    // TODO (TELE-1845). There's a discrepancy between IErrorPayload and the error message the SDK returns.
    // We'll need to either rectify the difference to pull the description or continue matching by codes if
    // we'd like to show our own messages.
    if (error.status === 404) {
      showErrorToast(
        'Conversation not found. Please navigate to the conversation from your inbox.'
      );
    } else {
      showErrorToast('Error loading conversation. Please contact support.');
    }
    history.push(PATIENT_MESSAGING_INBOX_PATH);
  });

  if (!currentUser || smoochGetConversationByIDQueryResponse.isLoading) {
    return <Spinner />;
  }

  Smooch.on('message', (message: IZendeskMessage, data: { conversation: IZendeskConversation }) => {
    // The WebSocket may unintentionally process multiple events for the same message. Currently, we've only seen
    // ordering maintained so checking the last ID should be sufficient, but we should test with many messages
    // coming in quick succession.
    if (data.conversation.id === conversation?.id && !conversation.messageIds.has(message.id)) {
      setMessages((prevMessages: IMessage[]) => {
        // Filter out messages that are hidden from patient or have no text content
        // Some messages may not have text (e.g. images)
        return !message.metadata?.is_hidden_from_patient && message.text
          ? [...prevMessages, convertZendeskMessageToInternalRepresentation(message)]
          : prevMessages;
      });
      setConversation((prevConversation: IConversationWithMessageIds) => {
        prevConversation.messageIds.add(message.id);
        return prevConversation;
      });
      updateMessageToRead({
        conversation_external_id: conversation.id,
        message_external_id: message.id,
      });
    }
  });

  logAnalyticsPageView('messaging - patient-dashboard - thread', {
    user: currentUser.id,
    conversation: conversationId,
    messages: messages.length,
  });

  return (
    <PageContainer helmetTitle="Conversation View" maxWidth="md" topBottomMarginVariant="large">
      <Conversation
        title={conversation?.participantNames.join(', ') || 'Care Team'}
        threadName={conversation?.displayName || ''}
        onBackButtonClick={() => {
          trackAnalyticsEvent('messaging - patient-dashboard - thread - back button clicked');
          history.push(PATIENT_MESSAGING_INBOX_PATH);
        }}
        onMessageSubmit={async (message?: string) => {
          trackAnalyticsEvent('messaging - patient-dashboard - thread - send message clicked', {
            user: currentUser.id,
            conversation: conversationId,
            messageLength: message ? message.length : 0,
          });
          if (message) {
            // The `sendMessage` method doesn't return an ID, so we intentionally don't add this to
            // the `messages` array and rely on the WebSocket to populate the message. If this proves
            // flakey, we can refactor this to make another request to fetch the latest message.
            smoochSendMessageInConversation(message, conversationId);
          }
        }}
        messages={messages}
      />
    </PageContainer>
  );
};
